/**
 * URL Unlocking Types
 * TypeScript interfaces for PageUrl API models
 */

/**
 * PageUrl type enum - matches backend PageUrlType
 */
export enum PageUrlType {
  Organization = 1,
  Campaign = 2,
  Event = 3,
  Fundraiser = 4,
  Team = 5,
  Collection = 6,
  Series = 7
}

/**
 * Target type for form - which URL segment to query
 */
export type UrlTargetType = 'primary' | 'secondary' | 'tertiary';

/**
 * Form mode - easy (single URL input) or advanced (separate inputs)
 */
export type FormMode = 'easy' | 'advanced';

/**
 * Linked entity information
 */
export interface PageUrlLinkedEntityModel {
  id: number;
  name: string;
  urlPath: string;
  status?: string | null;
}

/**
 * Main PageUrl element model from API
 */
export interface PageUrlElementModel {
  id: number;
  urlPath: string;
  urlType: PageUrlType;
  urlScopeKey: string;
  organizationId?: number;
  eventId?: number;
  campaignId?: number;
  fundraiserId?: number;
  teamId?: number;
  collectionId?: number;
  seriesId?: number;
  organization?: PageUrlLinkedEntityModel;
  event?: PageUrlLinkedEntityModel;
  campaign?: PageUrlLinkedEntityModel;
  fundraiser?: PageUrlLinkedEntityModel;
  team?: PageUrlLinkedEntityModel;
  collection?: PageUrlLinkedEntityModel;
  series?: PageUrlLinkedEntityModel;
}

/**
 * Paginated list wrapper from API
 */
export interface PagedList<T> {
  totalCount: number;
  page: number;
  pageSize: number;
  list: T[];
}

/**
 * API response wrapper
 */
export interface ApiResponse<T> {
  data: T;
  success: boolean;
  error: string | null;
}

/**
 * Public page data from /api/v2/page/public endpoint
 */
export interface PublicPageData {
  organization?: { id: number; name?: string; urlPath?: string };
  event?: { id: number; name?: string; urlPath?: string };
  campaign?: { id: number; name?: string; urlPath?: string };
  fundraiser?: { id: number; name?: string; urlPath?: string };
  collection?: { id: number; name?: string; urlPath?: string };
  series?: { id: number; name?: string; urlPath?: string };
}

/**
 * Entity IDs extracted from public page response
 */
export interface ExtractedEntityIds {
  organizationId?: number;
  eventId?: number;
  campaignId?: number;
  fundraiserId?: number;
  collectionId?: number;
  seriesId?: number;
}

/**
 * Form state for URL unlocking
 */
export interface UrlUnlockingFormState {
  mode: FormMode;
  targetType: UrlTargetType;
  // Easy mode
  fullUrl: string;
  // Advanced mode / parsed values
  primaryPath: string;
  secondaryPath: string;
  tertiaryPath: string;
}

/**
 * Query state for tracking API calls
 */
export interface QueryState {
  isLoading: boolean;
  error: string | null;
  publicData: PublicPageData | null;
  results: PageUrlElementModel[];
}
